import type {
  FlowSchema,
  FlowStepEnd,
  FlowStepLeadGate,
  FlowStepRadiogroup,
  FlowStepSkinScan,
} from './flow-schema.js';

export type FlowCompleteMeta = {
  flowId?: string;
  flowVersion?: number;
  endStep: FlowStepEnd | null;
};

export function isFlowSchemaV2(schema: unknown): schema is FlowSchema {
  return (
    !!schema &&
    typeof schema === 'object' &&
    (schema as FlowSchema).version === 2 &&
    Array.isArray((schema as FlowSchema).steps)
  );
}

export function getEndStep(schema: FlowSchema): FlowStepEnd | null {
  const end = schema.steps.find((s) => s.type === 'end');
  return end?.type === 'end' ? end : null;
}

export function prepareFlowForSurvey(
  schema: unknown,
): { surveyJson: Record<string, unknown>; endStep: FlowStepEnd | null } | null {
  if (isFlowSchemaV2(schema)) {
    const compiled = compileToSurveyJS(schema);
    if (!compiled) return null;
    return { surveyJson: compiled, endStep: getEndStep(schema) };
  }
  if (schema && typeof schema === 'object' && 'pages' in (schema as object)) {
    return { surveyJson: schema as Record<string, unknown>, endStep: null };
  }
  return null;
}

export function compileToSurveyJS(schema: FlowSchema): Record<string, unknown> | null {
  if (!schema?.steps?.length) return null;

  const surveyJson: Record<string, unknown> = {
    title: schema.meta?.title,
    description: schema.meta?.description,
    showProgressBar: 'top',
    progressBarType: 'buttons',
    showNavigationButtons: true,
    pages: [] as Record<string, unknown>[],
  };

  const pages = surveyJson.pages as Record<string, unknown>[];

  for (const step of schema.steps) {
    if (step.type === 'end') continue;

    const page: Record<string, unknown> = {
      name: step.id,
      elements: [] as Record<string, unknown>[],
    };

    const element: Record<string, unknown> = {
      name: step.id,
      title: step.title,
      description: step.description,
      isRequired: step.required,
    };

    switch (step.type) {
      case 'radiogroup':
        element.type = 'radiogroup';
        element.choices = step.choices.map((c) => ({ value: c.value, text: c.label }));
        break;
      case 'checkbox':
        element.type = 'checkbox';
        element.choices = step.choices.map((c) => ({ value: c.value, text: c.label }));
        break;
      case 'text':
        element.type = step.inputType === 'textarea' ? 'comment' : 'text';
        if (step.inputType === 'email') element.inputType = 'email';
        if (step.inputType === 'number') element.inputType = 'number';
        if (step.inputType === 'phone') element.inputType = 'tel';
        element.placeHolder = step.placeholder;
        break;
      case 'rating':
        element.type = 'rating';
        element.rateMin = step.rateMin;
        element.rateMax = step.rateMax;
        break;
      case 'imagepicker':
        element.type = 'imagepicker';
        element.choices = step.choices.map((c) => ({
          value: c.value,
          text: c.label,
          imageLink: c.imageUrl,
        }));
        element.multiSelect = step.multiSelect;
        break;
      case 'html':
        element.type = 'html';
        element.html = step.htmlContent;
        break;
      case 'skin-scan': {
        const scan = step as FlowStepSkinScan;
        element.type = 'skin-scan';
        element.mode = scan.mode ?? 'mock';
        element.captureDurationMs = scan.captureDurationMs ?? 3500;
        element.showMetrics = scan.showMetrics ?? ['hydration', 'texture', 'spots'];
        element.disclaimerHtml = scan.disclaimerHtml ?? '';
        break;
      }
      case 'lead-gate': {
        const gate = step as FlowStepLeadGate;
        element.type = 'panel';
        const panelElements: Record<string, unknown>[] = [];
        if (gate.fields?.includes('email')) {
          panelElements.push({
            type: 'text',
            inputType: 'email',
            name: 'email',
            title: 'Email',
            isRequired: gate.emailRequired !== false,
          });
        }
        if (gate.fields?.includes('phone')) {
          panelElements.push({
            type: 'text',
            inputType: 'tel',
            name: 'phone',
            title: 'Téléphone',
            isRequired: gate.phoneRequired === true,
          });
        }
        if (gate.fields?.includes('firstName')) {
          panelElements.push({
            type: 'text',
            name: 'firstName',
            title: 'Prénom',
            isRequired: true,
          });
        }
        if (gate.fields?.includes('lastName')) {
          panelElements.push({
            type: 'text',
            name: 'lastName',
            title: 'Nom',
            isRequired: true,
          });
        }
        if (gate.fields?.includes('name')) {
          panelElements.push({ type: 'text', name: 'name', title: 'Nom complet' });
        }
        if (gate.fields?.includes('company')) {
          panelElements.push({ type: 'text', name: 'company', title: 'Entreprise' });
        }
        if (gate.consentText) {
          panelElements.push({
            type: 'boolean',
            name: 'consent',
            title: gate.consentText,
            isRequired: true,
          });
        }
        element.elements = panelElements;
        element.title = gate.title;
        break;
      }
    }

    (page.elements as Record<string, unknown>[]).push(element);
    pages.push(page);
  }

  for (let i = 0; i < schema.steps.length; i++) {
    const step = schema.steps[i] as FlowStepRadiogroup;
    if (step.type !== 'radiogroup' || !step.branching) continue;

    for (const choiceVal of Object.keys(step.branching)) {
      const targetId = step.branching[choiceVal].skipTo;
      const targetIdx = schema.steps.findIndex((s) => s.id === targetId);

      if (targetIdx > i + 1) {
        for (let j = i + 1; j < targetIdx; j++) {
          const skippedStep = schema.steps[j];
          if (skippedStep.type === 'end') continue;

          const page = pages.find((p) => p.name === skippedStep.id);
          if (page) {
            const cond = `{${step.id}} != '${choiceVal}'`;
            page.visibleIf = page.visibleIf
              ? `(${page.visibleIf}) and (${cond})`
              : cond;
          }
        }
      }
    }
  }

  return surveyJson;
}
